import { reportInfo } from '@ms/yammer-telemetry';
import type { CaeCallSite, CaeOutcome } from '@ms/yammer-telemetry';

export type Challenges = Record<string, string>;

/**
 * Parses the `www-authenticate` header for CAE (Continuous Access Evaluation) challenges.
 * The format of the CAE header is `Bearer authorization_uri="{url}", error="insufficient_claims", claims="{base64}"`.
 */
export type ParseChallenges = (caeHeader: string | undefined) => Challenges | undefined;
export const parseChallenges: ParseChallenges = (caeHeader: string | undefined) => {
  if (!caeHeader?.includes('insufficient_claims')) {
    return undefined;
  }
  const schemeSeparator = caeHeader.indexOf(' ');
  const challenges = caeHeader.substring(schemeSeparator + 1).split(',');

  return challenges.reduce<Challenges>((challengeMap, challenge) => {
    const [key, value] = challenge.split('=');

    return { ...challengeMap, [key.trim()]: decodeURI(value.replace(/['"]+/g, '')) };
  }, {});
};

/**
 * Extracts the decoded claims string from a `www-authenticate` header. Uses a regex
 * targeted at the `claims="..."` segment to avoid comma-splitting issues with quoted
 * values like `trusted_issuers` that `parseChallenges` suffers from.
 */
export const getClaimsFromHeader = (wwwAuthenticate: string | undefined): string | undefined => {
  if (!wwwAuthenticate?.includes('insufficient_claims')) {
    return undefined;
  }

  const claimsMatch = /claims="([^"]+)"/.exec(wwwAuthenticate);
  const claimsChallenge = claimsMatch?.[1];

  // eslint-disable-next-line no-restricted-globals
  return claimsChallenge ? atob(claimsChallenge) : undefined;
};

const isErrorObject = (error: unknown): error is Readonly<Record<string, unknown>> =>
  typeof error === 'object' && error != null;

const getErrorStatus = (error: unknown): number | undefined =>
  isErrorObject(error) && typeof error.status === 'number' ? error.status : undefined;

const getErrorCode = (error: unknown): string | undefined =>
  isErrorObject(error) && typeof error.code === 'string' ? error.code : undefined;

const getErrorMessage = (error: unknown): string | undefined => (error instanceof Error ? error.message : undefined);

interface XhrLike {
  readonly getResponseHeader: (name: string) => string | null;
}

const isXhrLike = (value: unknown): value is XhrLike =>
  isErrorObject(value) && typeof value.getResponseHeader === 'function';

/**
 * Checks if an error is a CAE (Continuous Access Evaluation) claims challenge.
 * Works with GraphError from @ms/data-sources-graph-core which has status and code properties.
 * Requires both status 401 AND authChallengeRequired code to avoid false positives on other 401 errors.
 */
export const isClaimsChallengeError = (error: unknown): boolean =>
  getErrorStatus(error) === 401 && getErrorCode(error) === 'authChallengeRequired';

/**
 * Extracts the CAE claims from a GraphError's www-authenticate header.
 * The GraphError from @ms/data-sources-graph-core has a `request` property (XMLHttpRequest)
 * that we can use to access the response headers.
 *
 * @returns The decoded claims string to pass to MSAL, or undefined if not available.
 */
export const getClaimsFromGraphError = (error: unknown): string | undefined => {
  if (!isErrorObject(error)) {
    return undefined;
  }

  const request = error.request;
  const wwwAuthenticate = isXhrLike(request) ? (request.getResponseHeader('www-authenticate') ?? undefined) : undefined;

  return getClaimsFromHeader(wwwAuthenticate);
};

/**
 * Static context passed to executeWithCaeRetry so retry outcomes can be attributed
 * to a specific direct SharePoint call site in CAE telemetry.
 */
export interface CaeTelemetryContext {
  readonly callSite: CaeCallSite;
  /** Bare hostname of the request (e.g. `tenant.sharepoint.com`, no protocol), used for tenant-level breakdown in Kusto. */
  readonly host?: string;
}

interface ReportCaeClaimsChallengeOptions extends CaeTelemetryContext {
  readonly outcome: CaeOutcome;
  readonly errorCode?: string;
  readonly errorMessage?: string;
}

export const reportCaeClaimsChallenge = ({
  callSite,
  outcome,
  host,
  errorCode,
  errorMessage,
}: ReportCaeClaimsChallengeOptions): void => {
  reportInfo({
    eventName: 'cae_claims_challenge',
    eventProperties: {
      callSite,
      outcome,
      ...(host !== undefined && { host }),
      ...(errorCode !== undefined && { errorCode }),
      ...(errorMessage !== undefined && { errorMessage }),
    },
  });
};

/**
 * Executes an operation with automatic CAE (Continuous Access Evaluation) retry.
 * If the operation fails with a claims challenge, extracts the claims and retries once
 * with forceRefresh=true to get a fresh token.
 *
 * @param operation - Function that accepts optional claims and forceRefresh parameters.
 * @param telemetryContext - Optional context used to emit `cae_claims_challenge` telemetry around the retry.
 * @returns The result of the operation.
 */
export const executeWithCaeRetry = async <T>(
  operation: (claims: string | undefined, forceRefresh: boolean) => Promise<T>,
  telemetryContext?: CaeTelemetryContext
): Promise<T> => {
  try {
    return await operation(undefined, false);
  } catch (error) {
    if (!isClaimsChallengeError(error)) {
      if (telemetryContext && getErrorStatus(error) === 401) {
        reportCaeClaimsChallenge({
          ...telemetryContext,
          outcome: 'errorShapeUnrecognized',
          errorCode: getErrorCode(error),
          errorMessage: getErrorMessage(error),
        });
      }

      throw error;
    }

    if (telemetryContext) {
      reportCaeClaimsChallenge({ ...telemetryContext, outcome: 'detected' });
    }

    const claims = getClaimsFromGraphError(error);

    try {
      const result = await operation(claims, true);
      if (telemetryContext) {
        reportCaeClaimsChallenge({ ...telemetryContext, outcome: 'retrySucceeded' });
      }

      return result;
    } catch (retryError) {
      if (telemetryContext) {
        reportCaeClaimsChallenge({
          ...telemetryContext,
          outcome: 'retryFailed',
          errorCode: getErrorCode(retryError),
          errorMessage: getErrorMessage(retryError),
        });
      }

      throw retryError;
    }
  }
};
